import { Box, Stack, Typography, Button, alpha } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { WarningAmberRounded } from '@mui/icons-material';
import { useAppSelector } from '../../hooks/useAppStore';
import { esPlanGratis } from '../../utils/planGratis';

export const SUBSCRIPTION_BANNER_HEIGHT = 44;

/** ¿Debe mostrarse el banner de suscripción? (tenant booster con pago vencido/suspendido). */
export function useShowSubscriptionBanner(): boolean {
  const sub = useAppSelector((s) => s.auth.user?.subscription);
  // Un plan gratis no tiene pago que regularizar — ver `esPlanGratis`.
  if (!sub || sub.native || esPlanGratis(sub)) return false;
  return sub.status === 'suspended' || sub.status === 'canceled' || sub.status === 'past_due';
}

/**
 * Banner de estado de suscripción (tenants booster). Fijo debajo del topbar (recibe `top`
 * del layout, que además le hace lugar en el topOffset del contenido).
 */
export function SubscriptionBanner({ top = 56 }: { top?: number }) {
  const sub = useAppSelector((s) => s.auth.user?.subscription);
  if (!sub || sub.native || esPlanGratis(sub)) return null;

  const isSuspended = sub.status === 'suspended' || sub.status === 'canceled';
  const isPastDue   = sub.status === 'past_due';
  if (!isSuspended && !isPastDue) return null;

  // Rojo translúcido (glass) para suspendido; ámbar translúcido para pago vencido.
  const accent = isSuspended ? '#ef4444' : '#f59e0b';
  const text   = isSuspended ? '#fecaca' : '#fde68a';
  const msg = isSuspended
    ? 'Tu suscripción está suspendida — el CRM quedó en solo lectura. Regularizá el pago para seguir operando.'
    : 'Tu último pago no se procesó. Regularizalo para no perder el acceso.';

  return (
    <Box sx={{
      position: 'fixed', top, left: 0, right: 0,
      // Por encima del sidebar (drawer + 2, ver Sidebar.tsx): con 1200 le tapaba el
      // borde izquierdo al cartel. Mismo criterio que ImpersonationBanner. (03/09/2026)
      zIndex: (t: import('@mui/material').Theme) => t.zIndex.drawer + 3,
      height: SUBSCRIPTION_BANNER_HEIGHT,
      bgcolor: alpha(accent, 0.13),
      backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
      borderBottom: `1px solid ${alpha(accent, 0.38)}`,
      color: text, px: 2, display: 'flex', alignItems: 'center',
    }}>
      <Stack direction="row" alignItems="center" spacing={1.5} justifyContent="center" flexWrap="wrap" sx={{ width: '100%' }}>
        <WarningAmberRounded fontSize="small" sx={{ color: accent }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{msg}</Typography>
        <Button size="small" component={RouterLink} to="/profile?tab=subscription" sx={{
          py: 0.1, textTransform: 'none', fontWeight: 700, color: text,
          border: `1px solid ${alpha(accent, 0.5)}`,
          '&:hover': { bgcolor: alpha(accent, 0.18), borderColor: accent },
        }}>
          Renovar suscripción
        </Button>
      </Stack>
    </Box>
  );
}
